import CalendarRoomListPicker from './CalendarRoomListPicker';
import * as strings from './CapabilityFilters.locstring.json';
import CapacityFilter from './CapacityFilter';
import FeatureFilter from './FeatureFilter';
import FloorFilter from './FloorFilter';
import PlaceTypeFilter from './PlaceTypeFilter';
import { searchRoomsUsingPlacesApi } from '../../actions/calendarRoomActions';
import {
    clearFilters,
    updateShouldGetSpaces,
    updateSelectedFilters,
} from '../../mutators/capabilityFilterMutators';
import getRoomlistCapabilities from '../../selectors/getRoomlistCapabilities';
import isAnyFilterApplied from '../../selectors/isAnyFilterApplied';
import getPlaceholderRoomLists from '../../selectors/getPlaceholderRoomLists';
import getStore from '../../store/store';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';
import type RoomFilterWellItemViewState from '../RoomSelector/RoomFilterWellItemViewState';
import { observer } from 'mobx-react-lite';
import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { onOptionSelected } from 'owa-calendar-composite-picker';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import loc from 'owa-localize';
import * as React from 'react';
import {
    isFilterApplied,
    updateCapabilitiesAvailability,
} from '../../mutators/roomPanelMetricsMutators';
import { useModeStyles } from 'owa-react-hooks/lib/useModeStyles';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ControlIcons } from 'owa-control-icons';
import { useToggleState } from 'owa-react-hooks/lib/useToggleState';
import { logUsage } from 'owa-analytics';

const styles = {
    base: require('./RoomFilterV2.scss'),
    desktop: require('./RoomFilterV2.outlookdesktop.scss'),
    mac: require('./RoomFilterV2.macoutlook.scss'),
};

export interface RoomFilterV2Props {
    colorScheme: ColorScheme;
    onRoomFilterWellItemsChanged: (items: RoomFilterWellItemViewState[]) => void;
    onPlaceTypeChanged?: (isWorkspace: boolean) => void;
}

const RoomFilterV2: React.FunctionComponent<RoomFilterV2Props> = props => {
    const { getClass } = useClientStyles(styles);
    const { getModeAwareClass } = useModeStyles(getClass);
    const capabilities = getRoomlistCapabilities();
    const showPlaceType: boolean = getStore().roomListViewState.showPlaceType;
    const inSpacesMode: boolean = getStore().roomFilterState.shouldGetSpaces;
    const [isExpanded, toggleIsExpanded] = useToggleState(true);
    const onClearFilters = React.useCallback(() => {
        onOptionSelected(getStore().roomListPickerViewState, null, null);
        clearFilters();
        props.onRoomFilterWellItemsChanged([]);
        isFilterApplied();
        if (props.onPlaceTypeChanged) {
            props.onPlaceTypeChanged(false);
        }
    }, [props.onRoomFilterWellItemsChanged]);

    const onPlaceTypeChanged = React.useCallback(
        (shouldGetSpaces: boolean) => {
            if (props.onPlaceTypeChanged) {
                props.onPlaceTypeChanged(shouldGetSpaces);
            }
            onChangeShouldGetSpaces(shouldGetSpaces);
        },
        [props.onPlaceTypeChanged]
    );

    return (
        <>
            <IconButton
                className={getModeAwareClass('expandButton')}
                onClick={toggleIsExpanded}
                title={
                    isExpanded ? loc(strings.roomFiltersCollapse) : loc(strings.roomFiltersExpand)
                }
                iconProps={{
                    iconName: isExpanded ? ControlIcons.ChevronDown : ControlIcons.ChevronUp,
                    styles: { root: { fontSize: '13px' } },
                }}
            />
            <ActionButton
                className={getModeAwareClass('clearFiltersButton')}
                onClick={onClearFilters}
                disabled={!isAnyFilterApplied()}>
                {loc(strings.clearCapabilityFiltersButtonText)}
            </ActionButton>

            <div className={getModeAwareClass('filterHeader')}>
                {isExpanded ? loc(strings.roomListsFilterLabel) : loc(strings.roomFiltersCollapsed)}
            </div>
            {isExpanded && (
                <div className={getModeAwareClass('filterHeader')}>
                    <CalendarRoomListPicker
                        colorScheme={props.colorScheme}
                        onChanged={props.onRoomFilterWellItemsChanged}
                        placeholder={loc(strings.roomListFilterPlaceholder)}
                        pickerAriaLabel={loc(strings.roomListFilterPlaceholder)}
                    />
                    {isFeatureEnabled('cal-mf-usePlacesVNext') &&
                        isFeatureEnabled('cal-mf-spaces') &&
                        showPlaceType && (
                            <>
                                <div className={getModeAwareClass('filterHeader')}>
                                    {loc(strings.placeTypeFilterLabel)}
                                </div>
                                <PlaceTypeFilter
                                    shouldGetSpaces={inSpacesMode}
                                    onChange={onPlaceTypeChanged}
                                />
                            </>
                        )}
                    <div className={getClass('row')}>
                        <div className={getClass('column')}>
                            <div className={getModeAwareClass('filterHeader')}>
                                {loc(strings.capacityFilterLabel)}
                            </div>
                            <CapacityFilter
                                maxCapacity={capabilities.maxCapacity}
                                onChange={onApplyFilters}
                            />
                        </div>
                        <div className={getClass('column')}>
                            <div className={getModeAwareClass('filterHeader')}>
                                {loc(strings.floorFilterLabel)}
                            </div>
                            <FloorFilter
                                minFloor={capabilities.minFloor}
                                maxFloor={capabilities.maxFloor}
                                onChange={onApplyFilters}
                            />
                        </div>
                    </div>
                    <div className={getModeAwareClass('filterHeader')}>
                        {loc(strings.featureFilterLabel)}
                    </div>
                    <FeatureFilter
                        theme={themeFromColorScheme(props.colorScheme)}
                        hasFeatures={capabilities.hasFeatures}
                        onChange={onApplyFeatureFilters}
                        disabled={inSpacesMode}
                        placeholder={
                            inSpacesMode
                                ? loc(strings.featureFilterDisabledForSpaces)
                                : containsFeatures(capabilities.hasFeatures)
                                ? loc(strings.featureFilterPlaceHolder)
                                : loc(strings.featuresFilterNoFeaturePlaceholder)
                        }
                    />
                </div>
            )}
        </>
    );
};

function containsFeatures(hasFeatures: boolean[]) {
    return hasFeatures.some(feature => feature == true);
}

function onApplyFeatureFilters() {
    onApplyFilters();
    updateCapabilitiesAvailability();
}

function onApplyFilters() {
    updateSelectedFilters();
    isFilterApplied();
}

function onChangeShouldGetSpaces(shouldGetSpaces: boolean) {
    clearFilters();
    logUsage('QueryingPlaceType', {
        type: shouldGetSpaces ? 'Workspace' : 'ConferenceRoom',
    });
    updateShouldGetSpaces(shouldGetSpaces);
    searchRoomsUsingPlacesApi({
        roomLists: getStore().roomListPickerViewState.selectedOption
            ? [
                  {
                      email: getStore().roomListPickerViewState.selectedOption?.id,
                      name: null,
                  },
              ]
            : getPlaceholderRoomLists(),
        showOnlyAvailableRooms: true,
    });
}

export default observer(RoomFilterV2);
